/**
 * Reports page.
 *
 * - Every user sees the report variants assigned to them as small cards.
 * - Admins additionally see: a "+ New report" CTA, edit/delete on each card,
 *   and a collapsible ad-hoc explorer for one-off slicing.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
    ChartBar, ChartLine, ChartPie, Plus, PencilSimple, Trash, Sparkle, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ReportVariantDialog } from "@/components/app/ReportVariantDialog";
import { ReportVariantRunModal } from "@/components/app/ReportVariantRunModal";

const COLORS = ["#002FA7", "#F4B400", "#0F9D58", "#DB4437", "#9C27B0", "#00BCD4", "#FF6F00"];
const NUMERIC = new Set(["number", "decimal", "currency"]);

const CHART_ICONS = { bar: ChartBar, line: ChartLine, pie: ChartPie };

export default function ReportsPage() {
    const { user } = useAuth();
    const isAdmin = ["platform_admin", "org_admin"].includes(user?.role);
    const [searchParams, setSearchParams] = useSearchParams();

    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);

    // dialogs
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = new, object = edit
    const [runOpen, setRunOpen] = useState(false);
    const [runVariant, setRunVariant] = useState(null);

    // ad-hoc explorer
    const [showExplorer, setShowExplorer] = useState(false);
    const [forms, setForms] = useState([]);
    const [formId, setFormId] = useState("");
    const [summary, setSummary] = useState(null);
    const [groupBy, setGroupBy] = useState("__created_day__");
    const [metricOp, setMetricOp] = useState("count");
    const [metricField, setMetricField] = useState("__none__");
    const [chartType, setChartType] = useState("bar");
    const [agg, setAgg] = useState(null);

    const loadVariants = async () => {
        try {
            const { data } = await api.get("/report-variants/mine");
            setVariants(data);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { loadVariants(); }, []);

    // Auto-open variant if `?open=<id>` is in URL (sidebar tree deeplink)
    useEffect(() => {
        const target = searchParams.get("open");
        if (!target || !variants.length) return;
        const v = variants.find((x) => x.id === target);
        if (v) {
            setRunVariant(v); setRunOpen(true);
            // strip param so closing the modal doesn't re-trigger
            searchParams.delete("open");
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line
    }, [variants, searchParams]);

    // Lazy-load explorer data only when admin opens it
    useEffect(() => {
        if (!showExplorer || !isAdmin || forms.length) return;
        (async () => {
            try {
                const { data } = await api.get("/forms");
                setForms(data);
                if (data.length) setFormId(data[0].id);
            } catch (e) { toast.error(extractError(e)); }
        })();
        // eslint-disable-next-line
    }, [showExplorer, isAdmin]);

    useEffect(() => {
        if (!formId) return;
        (async () => {
            try {
                const { data } = await api.get(`/reports/${formId}/summary`);
                setSummary(data);
                setGroupBy("__created_day__"); setMetricOp("count"); setMetricField("__none__");
                setAgg(null);
            } catch (e) { toast.error(extractError(e)); }
        })();
    }, [formId]);

    const runAgg = async () => {
        if (!formId || !groupBy) return;
        try {
            const payload = {
                group_by: groupBy,
                metric_op: metricOp,
                metric_field: metricOp === "count" ? null : (metricField === "__none__" ? null : metricField),
            };
            const { data } = await api.post(`/reports/${formId}/aggregate`, payload);
            setAgg(data);
        } catch (e) { toast.error(extractError(e)); }
    };

    const numericFields = useMemo(
        () => (summary?.available_fields || []).filter((f) => NUMERIC.has(f.type)),
        [summary],
    );

    const openNew = () => { setEditing(null); setEditorOpen(true); };
    const openEdit = (v) => { setEditing(v); setEditorOpen(true); };
    const remove = async (v) => {
        if (!confirm(`Delete report "${v.name}"?`)) return;
        try { await api.delete(`/report-variants/${v.id}`); toast.success("Report deleted"); await loadVariants(); }
        catch (e) { toast.error(extractError(e)); }
    };
    const run = (v) => { setRunVariant(v); setRunOpen(true); };

    const saveFromExplorer = async () => {
        const draft = {
            form_id: formId, group_by: groupBy,
            metric_op: metricOp,
            metric_field: metricOp === "count" ? null : (metricField === "__none__" ? null : metricField),
            chart_type: chartType,
        };
        setEditing({ ...draft, name: "", description: "", access_user_ids: [], access_group_ids: [], access_role_ids: [] });
        setEditorOpen(true);
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Insights"
                title="Reports"
                description={isAdmin ? "Save reusable report variants and assign them to users via groups or roles." : "Reports assigned to you."}
                actions={isAdmin ? (
                    <Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-report-btn">
                        <Plus size={16} className="mr-2" /> New report
                    </Button>
                ) : null}
                testid="reports-header"
            />

            {/* Variant cards grid */}
            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : variants.length === 0 ? (
                <EmptyState
                    icon={ChartBar}
                    title={isAdmin ? "No reports yet" : "No reports assigned"}
                    description={isAdmin
                        ? "Create your first report variant. Or use the ad-hoc explorer below."
                        : "Ask your admin to assign reports to your group or role."
                    }
                    testid="reports-empty"
                />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="report-variants-grid">
                    {variants.map((v) => {
                        const Icon = CHART_ICONS[v.chart_type] || ChartBar;
                        return (
                            <button
                                key={v.id} type="button"
                                onClick={() => run(v)}
                                className="brut-card p-5 text-left hover:border-primary/40 group transition-colors"
                                data-testid={`report-card-${v.id}`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <Icon size={20} weight="duotone" className="text-primary" />
                                    </div>
                                    {isAdmin && (
                                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                onClick={(e) => { e.stopPropagation(); openEdit(v); }}
                                                className="h-7 w-7 rounded-sm hover:bg-secondary inline-flex items-center justify-center cursor-pointer"
                                                data-testid={`edit-report-${v.id}`}
                                            >
                                                <PencilSimple size={12} />
                                            </span>
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                onClick={(e) => { e.stopPropagation(); remove(v); }}
                                                className="h-7 w-7 rounded-sm hover:bg-destructive/10 text-destructive inline-flex items-center justify-center cursor-pointer"
                                                data-testid={`del-report-${v.id}`}
                                            >
                                                <Trash size={12} />
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="font-semibold text-sm tracking-tight leading-tight mb-1">{v.name}</div>
                                <div className="text-[11px] font-mono text-muted-foreground truncate mb-2">{v.form_name || v.form_id}</div>
                                {v.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{v.description}</p>}
                                <div className="flex items-center gap-1 mt-3 flex-wrap">
                                    {v.kind === "multi" ? (
                                        <>
                                            <Badge variant="default" className="rounded-sm font-mono text-[9px] bg-primary text-primary-foreground">multi-form</Badge>
                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{(v.joins || []).length} join{(v.joins || []).length === 1 ? "" : "s"}</Badge>
                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{(v.multi_metrics || []).length || 1} metric{(v.multi_metrics || []).length === 1 ? "" : "s"}</Badge>
                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{v.chart_type}</Badge>
                                        </>
                                    ) : (
                                        <>
                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{v.metric_op}</Badge>
                                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{v.group_by === "__created_day__" ? "by day" : `by ${v.group_by}`}</Badge>
                                        </>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Ad-hoc explorer (admin only) */}
            {isAdmin && (
                <div className="mt-10 border-t border-border pt-6">
                    <button
                        type="button"
                        onClick={() => setShowExplorer((s) => !s)}
                        className="flex items-center gap-2 text-sm font-semibold hover:text-primary transition-colors mb-4"
                        data-testid="toggle-explorer-btn"
                    >
                        {showExplorer ? <CaretUp size={14} /> : <CaretDown size={14} />}
                        <Sparkle size={14} weight="duotone" /> Ad-hoc explorer
                    </button>

                    {showExplorer && (
                        <div className="brut-section p-6" data-testid="reports-explorer">
                            <div className="flex flex-wrap items-end gap-3 mb-4">
                                <div className="min-w-[200px] flex-1">
                                    <Label className="label-overline">Form</Label>
                                    <Select value={formId} onValueChange={setFormId}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="reports-form-select"><SelectValue placeholder="Pick a form" /></SelectTrigger>
                                        <SelectContent>
                                            {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {summary && (
                                    <>
                                        <div>
                                            <Label className="label-overline">Group by</Label>
                                            <Select value={groupBy} onValueChange={setGroupBy}>
                                                <SelectTrigger className="mt-2 rounded-sm w-44" data-testid="reports-group-select"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__created_day__">Submission day</SelectItem>
                                                    {summary.available_fields.map((f) => (
                                                        <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="label-overline">Metric</Label>
                                            <Select value={metricOp} onValueChange={(v) => { setMetricOp(v); if (v === "count") setMetricField("__none__"); }}>
                                                <SelectTrigger className="mt-2 rounded-sm w-40" data-testid="reports-metric-op"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="count">Count</SelectItem>
                                                    <SelectItem value="sum" disabled={numericFields.length === 0}>Sum</SelectItem>
                                                    <SelectItem value="avg" disabled={numericFields.length === 0}>Average</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {metricOp !== "count" && (
                                            <div>
                                                <Label className="label-overline">Numeric field</Label>
                                                <Select value={metricField} onValueChange={setMetricField}>
                                                    <SelectTrigger className="mt-2 rounded-sm w-40" data-testid="reports-metric-field"><SelectValue placeholder="Field" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="__none__">(none)</SelectItem>
                                                        {numericFields.map((f) => <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                        <div>
                                            <Label className="label-overline">Chart</Label>
                                            <Select value={chartType} onValueChange={setChartType}>
                                                <SelectTrigger className="mt-2 rounded-sm w-28" data-testid="reports-chart-type"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="bar">Bar</SelectItem>
                                                    <SelectItem value="line">Line</SelectItem>
                                                    <SelectItem value="pie">Pie</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Button onClick={runAgg} variant="outline" className="rounded-sm" data-testid="run-report-btn">Run</Button>
                                        <Button onClick={saveFromExplorer} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-as-variant-btn">
                                            Save as report
                                        </Button>
                                    </>
                                )}
                            </div>

                            {agg && (
                                <div className="pt-4 border-t border-border">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-xs text-muted-foreground">
                                            {agg.metric_op === "count" ? "Records" : `${agg.metric_op} of ${agg.metric_field}`} by {agg.group_by === "__created_day__" ? "day" : agg.group_by}
                                            {agg.total_value != null && <> · <span className="font-mono">total {agg.total_value.toLocaleString()}</span></>}
                                        </div>
                                        <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{agg.rows.length} rows</Badge>
                                    </div>
                                    {agg.rows.length === 0 ? (
                                        <div className="text-sm text-muted-foreground">No data.</div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={280}>
                                            {chartType === "bar" ? (
                                                <BarChart data={agg.rows}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                    <XAxis dataKey="key" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={60} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip />
                                                    <Bar dataKey="value" fill={COLORS[0]} />
                                                </BarChart>
                                            ) : chartType === "line" ? (
                                                <LineChart data={agg.rows}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                                    <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip />
                                                    <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={2} />
                                                </LineChart>
                                            ) : (
                                                <PieChart>
                                                    <Pie data={agg.rows} dataKey="value" nameKey="key" outerRadius={100} label={(e) => `${e.key} (${e.value})`}>
                                                        {agg.rows.map((row, i) => <Cell key={row.key} fill={COLORS[i % COLORS.length]} />)}
                                                    </Pie>
                                                    <Tooltip />
                                                    <Legend />
                                                </PieChart>
                                            )}
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <ReportVariantDialog
                open={editorOpen}
                onOpenChange={setEditorOpen}
                initial={editing}
                onSaved={loadVariants}
            />

            <ReportVariantRunModal
                open={runOpen}
                onOpenChange={setRunOpen}
                variant={runVariant}
            />
        </PageContainer>
    );
}
